import type {
  RuleBoxAdditionReason,
  RuleBoxHistoryEntry,
  RuleBoxHistoryOutcome,
  RuleBoxHistoryTrigger,
  RuleBoxRemovalReason,
} from "@loot-os/api-contract";
import { dollars } from "@loot-os/api-contract/shopifyRuleBoxForm";
import type { StatusLabel } from "./labels";

/** What set a refresh off, as the start of a headline. */
export const HISTORY_TRIGGER: Record<RuleBoxHistoryTrigger, string> = {
  created: "Box created",
  rules_changed: "Rules changed",
  manual: "Refresh now",
  daily: "Daily refresh",
  value_change: "Price change picked up",
  stock_change: "Stock change",
  product_change: "Product updated in your store",
  renamed: "Box renamed",
};

/** How a refresh ended, as a badge. */
export const HISTORY_OUTCOME: Record<RuleBoxHistoryOutcome, StatusLabel> = {
  published: { label: "Updated", tone: "success" },
  unchanged: { label: "No changes", tone: "neutral" },
  refused: { label: "Couldn't rebuild", tone: "critical" },
};

/**
 * An entry's badge: how its refresh ended, or "Renamed" for a rename, which is not a refresh and
 * whose `unchanged` outcome would read as "nothing happened".
 */
export function historyBadge(entry: Pick<RuleBoxHistoryEntry, "trigger" | "outcome">): StatusLabel {
  return entry.trigger === "renamed"
    ? { label: "Renamed", tone: "info" }
    : HISTORY_OUTCOME[entry.outcome];
}

/** Why a product joined the box. */
export const ADDITION_REASON: Record<RuleBoxAdditionReason, string> = {
  new_product: "New in your store",
  back_in_stock: "Back in stock",
  now_matches: "Now matches the rules",
  now_above_min_return: "Now worth enough for the box's price",
  sampled: "Picked to fill the box",
};

/** Why a product left the box. */
export const REMOVAL_REASON: Record<RuleBoxRemovalReason, string> = {
  out_of_stock: "Sold out",
  removed_from_shop: "Deleted from your store",
  product_unavailable: "Unpublished or can't be shipped",
  no_longer_matches: "No longer matches the rules",
  below_min_return: "Worth too little for the box's price",
  not_sampled: "Left out to keep the box within its product limit",
};

type Actor = RuleBoxHistoryEntry["actor"];

/**
 * Who set a refresh off: "You" for the shop's staff (a label only when it reads as a name, never
 * a bare Shopify user id), "Loot OS staff", or "Automatic".
 */
export function actorLabel(actor: Actor): string {
  switch (actor.kind) {
    case "merchant":
      return actor.label && !/^(\d+|gid:\/\/.*)$/.test(actor.label.trim()) ? actor.label : "You";
    case "staff":
      return "Loot OS staff";
    case "system":
      return "Automatic";
  }
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** "v12 → v13", "v1" for a first version, or null when the version did not move. */
export function versionChange(entry: Pick<RuleBoxHistoryEntry, "fromVersion" | "toVersion">) {
  const { fromVersion: from, toVersion: to } = entry;
  if (to === null || to === from) return null;
  return from === null || from === 0 ? `v${to}` : `v${from} → v${to}`;
}

/** "$75.00 → $80.00", "$80.00" for a first price, or null when the price did not move. */
export function priceChange(
  entry: Pick<RuleBoxHistoryEntry, "priceFromCents" | "priceToCents">,
): string | null {
  const { priceFromCents: from, priceToCents: to } = entry;
  if (to === null || to === from) return null;
  return from === null ? dollars(to) : `${dollars(from)} → ${dollars(to)}`;
}

/** "10 → 12 products", "12 products" for a first table, or null when the count did not move. */
export function itemCountChange(
  entry: Pick<RuleBoxHistoryEntry, "itemCountFrom" | "itemCountTo">,
): string | null {
  const { itemCountFrom: from, itemCountTo: to } = entry;
  if (to === null || to === from) return null;
  const products = plural(to, "product", "products");
  return from === null ? products : `${from} → ${products}`;
}

/** The subject of a headline: who did it ("Loot OS" for a job, which is nobody's click). */
function subject(actor: Actor): string {
  return actor.kind === "system" ? "Loot OS" : actorLabel(actor);
}

/** What set it off, as the headline's lead. */
function lead(entry: RuleBoxHistoryEntry): string {
  const who = subject(entry.actor);
  switch (entry.trigger) {
    case "created":
      return `${who} created the box`;
    case "rules_changed":
      return `${who} changed the rules${entry.cause ? ` (${entry.cause})` : ""}`;
    case "manual":
      return `${who} refreshed the box`;
    case "daily":
      return "Daily refresh";
    case "value_change":
      return "Price change picked up";
    case "stock_change":
      return entry.cause ? `Stock change: ${entry.cause}` : "Stock change";
    case "product_change":
      return entry.cause ? `Product updated: ${entry.cause}` : "Product updated in your store";
    case "renamed":
      return `${who} renamed the box${entry.cause ? `: ${entry.cause}` : ""}`;
  }
}

/** A published refresh's changes in a few words: "3 products added, 1 removed, new price $80.00". */
function publishedSummary(entry: RuleBoxHistoryEntry): string {
  const { added, removed, revalued } = entry.changes;
  const parts: string[] = [];
  if (entry.trigger === "created") {
    if (entry.itemCountTo !== null) parts.push(plural(entry.itemCountTo, "product", "products"));
  } else {
    if (added.length > 0) parts.push(`${plural(added.length, "product", "products")} added`);
    if (removed.length > 0) {
      parts.push(
        added.length > 0
          ? `${removed.length} removed`
          : `${plural(removed.length, "product", "products")} removed`,
      );
    }
  }
  if (entry.priceToCents !== null && entry.priceToCents !== entry.priceFromCents) {
    parts.push(
      entry.trigger === "created"
        ? `priced at ${dollars(entry.priceToCents)}`
        : `new price ${dollars(entry.priceToCents)}`,
    );
  }
  if (parts.length > 0) return parts.join(", ");
  return revalued.length > 0 ? "odds re-balanced" : "rebuilt";
}

/**
 * One product going out of stock or coming back, the commonest webhook refresh, read as the
 * product: "Sold out: Rolex Oyster Keychain — removed from the box".
 */
function stockHeadline(entry: RuleBoxHistoryEntry): string | null {
  if (entry.trigger !== "stock_change" || entry.outcome !== "published") return null;
  const { added, removed } = entry.changes;
  const price =
    entry.priceToCents !== null && entry.priceToCents !== entry.priceFromCents
      ? `, new price ${dollars(entry.priceToCents)}`
      : "";
  const [gone] = removed;
  if (gone && removed.length === 1 && added.length === 0 && gone.reason === "out_of_stock") {
    return `Sold out: ${gone.name} — removed from the box${price}`;
  }
  const [back] = added;
  if (back && added.length === 1 && removed.length === 0 && back.reason === "back_in_stock") {
    return `Back in stock: ${back.name} — added to the box${price}`;
  }
  return null;
}

/** A history entry in one plain sentence, from what set it off and how it ended. */
export function historyHeadline(entry: RuleBoxHistoryEntry): string {
  const stock = stockHeadline(entry);
  if (stock) return stock;
  if (entry.trigger === "renamed") return lead(entry);
  switch (entry.outcome) {
    case "published":
      return `${lead(entry)} — ${publishedSummary(entry)}`;
    case "unchanged": {
      // The same products, but the processor may have revalued some between the two refreshes.
      const n = entry.changes.revalued.length;
      return n > 0
        ? `${lead(entry)} — same products, ${n} price${n === 1 ? "" : "s"} updated`
        : `${lead(entry)} — no changes`;
    }
    case "refused":
      return `${lead(entry)} — couldn't build the box: ${
        entry.refusalMessage ?? "no set of products fits the rules right now"
      }`;
  }
}

/** Whether an entry has a list of products to expand. */
export function hasChangeDetail(entry: RuleBoxHistoryEntry): boolean {
  const { added, removed, revalued } = entry.changes;
  return added.length + removed.length + revalued.length > 0;
}
